
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlusCircle, Filter, Calendar, FileText, Phone, Mail, Video, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { AddInteractionDialog } from './AddInteractionDialog';
import { useCandidateInteractions } from '@/hooks/useCandidateInteractions';
import { LoadingState } from '@/components/error-handling/LoadingState';
import { ErrorState } from '@/components/error-handling/ErrorState';
import type { CandidateInteraction } from '../types';

interface EnhancedInteractionHistoryProps {
  candidateId: string;
}

const getInteractionIcon = (type: string) => {
  switch (type) {
    case 'email': return <Mail className="h-4 w-4" />;
    case 'call': return <Phone className="h-4 w-4" />;
    case 'meeting': return <Video className="h-4 w-4" />;
    case 'note': return <MessageSquare className="h-4 w-4" />;
    case 'document': return <FileText className="h-4 w-4" />;
    default: return <MessageSquare className="h-4 w-4" />;
  }
};

const getInteractionColor = (type: string) => {
  switch (type) {
    case 'email': return 'bg-blue-100 text-blue-700';
    case 'call': return 'bg-green-100 text-green-700';
    case 'meeting': return 'bg-purple-100 text-purple-700';
    case 'note': return 'bg-gray-100 text-gray-700';
    case 'document': return 'bg-orange-100 text-orange-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const EnhancedInteractionHistory: React.FC<EnhancedInteractionHistoryProps> = ({ candidateId }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  const { 
    data: interactions = [], 
    isLoading, 
    error,
    refetch: refetchInteractions 
  } = useCandidateInteractions(candidateId);

  if (isLoading) return <LoadingState message="Loading interactions..." />;
  if (error) return <ErrorState title="Error loading interactions" description={error.message} onRetry={refetchInteractions} />;

  const filterInteractions = (interactions: CandidateInteraction[], filter: string) => {
    if (filter === 'all') return interactions;
    return interactions.filter(interaction => interaction.type === filter);
  };

  const filteredInteractions = filterInteractions(interactions, activeFilter);

  const interactionTypes = [
    { value: 'all', label: 'All', count: interactions.length },
    { value: 'email', label: 'Emails', count: interactions.filter(i => i.type === 'email').length },
    { value: 'call', label: 'Calls', count: interactions.filter(i => i.type === 'call').length },
    { value: 'meeting', label: 'Meetings', count: interactions.filter(i => i.type === 'meeting').length },
    { value: 'note', label: 'Notes', count: interactions.filter(i => i.type === 'note').length },
    { value: 'document', label: 'Documents', count: interactions.filter(i => i.type === 'document').length }
  ];

  const InteractionItem = ({ interaction }: { interaction: CandidateInteraction }) => (
    <div className="border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${getInteractionColor(interaction.type)}`}>
            {getInteractionIcon(interaction.type)}
          </div>
          <div>
            <h4 className="font-medium">{interaction.title}</h4>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {format(new Date(interaction.created_at), 'MMM d, yyyy h:mm a')}
            </div>
          </div>
        </div>
        <Badge className={getInteractionColor(interaction.type)}>
          {interaction.type}
        </Badge>
      </div>
      
      {interaction.content && (
        <p className="text-sm text-muted-foreground pl-11">
          {interaction.content}
        </p>
      )}
      
      {interaction.document_url && (
        <div className="pl-11">
          <Button size="sm" variant="outline" className="h-8">
            <FileText className="h-3 w-3 mr-1" />
            View Attachment
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Interaction Timeline
          </CardTitle>
          <Button size="sm" onClick={() => setIsAddDialogOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Interaction
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeFilter} onValueChange={setActiveFilter}>
          <TabsList className="grid w-full grid-cols-6">
            {interactionTypes.map(type => (
              <TabsTrigger key={type.value} value={type.value} className="text-xs">
                {type.label} ({type.count})
              </TabsTrigger>
            ))}
          </TabsList>
          
          <TabsContent value={activeFilter} className="mt-6">
            {filteredInteractions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No {activeFilter === 'all' ? '' : activeFilter} interactions found</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  Add First Interaction
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredInteractions.map((interaction) => (
                  <InteractionItem key={interaction.id} interaction={interaction} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <AddInteractionDialog
        candidateId={candidateId}
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onInteractionAdded={refetchInteractions}
      />
    </Card>
  );
};
